import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/shared/utils/showToast';
import { useAuthStore } from '@/store/authStore';
import {
  addCartItem,
  fetchCart,
  removeCartItem,
  removeCartItems,
  updateCartItem,
} from '../apis/cart';
import type { Cart, CartItem } from '../types/cart.types';

const CART_MUTATION_KEY = ['cart-change'];

// Per user, so a cart never shows up for the next account on the same browser
const useCartKey = () => ['cart', useAuthStore((state) => state.user_id)];

/** The signed-in user's cart; a visitor has none and nothing is requested. */
export const useCart = () => {
  const isLogin = useAuthStore((state) => state.isLogin);
  return useQuery({ queryKey: useCartKey(), queryFn: fetchCart, enabled: isLogin });
};

/** Header badge: number of copies in the cart, undefined for a visitor. */
export const useCartCount = () => useCart().data?.total_quantity;

const withItems = (cart: Cart, items: CartItem[]): Cart => ({
  ...cart,
  items,
  line_count: items.length,
  total_quantity: items.reduce((sum, item) => sum + item.quantity, 0),
});

/** Every cart change answers the whole cart. `optimistic` shows the change before the answer
 *  arrives; a failed change reloads the cart from the server. */
const useCartMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<Cart>,
  optimistic?: (cart: Cart, variables: TVariables) => Cart
) => {
  const queryClient = useQueryClient();
  const queryKey = useCartKey();
  return useMutation({
    mutationKey: CART_MUTATION_KEY,
    mutationFn,
    onMutate: async (variables) => {
      if (!optimistic) return;
      await queryClient.cancelQueries({ queryKey });
      const cart = queryClient.getQueryData<Cart>(queryKey);
      if (cart) queryClient.setQueryData(queryKey, optimistic(cart, variables));
    },
    onSuccess: (cart) => {
      // The answer to an earlier change would undo a later one still in flight
      if (queryClient.isMutating({ mutationKey: CART_MUTATION_KEY }) === 1) {
        queryClient.setQueryData(queryKey, cart);
      }
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey });
      showToast({ type: 'error', title: 'Gagal memperbarui keranjang', message: error.message });
    },
  });
};

export const useAddCartItem = () =>
  useCartMutation(({ productId, quantity }: { productId: string; quantity?: number }) =>
    addCartItem(productId, quantity)
  );

export const useUpdateCartItem = () =>
  useCartMutation(
    ({ itemId, quantity }: { itemId: string; quantity: number }) =>
      updateCartItem(itemId, quantity),
    (cart, { itemId, quantity }) =>
      withItems(
        cart,
        cart.items.map((item) =>
          item.id === itemId
            ? { ...item, quantity, line_total: (Number(item.unit_price) * quantity).toFixed(2) }
            : item
        )
      )
  );

export const useRemoveCartItem = () =>
  useCartMutation(removeCartItem, (cart, itemId) =>
    withItems(
      cart,
      cart.items.filter((item) => item.id !== itemId)
    )
  );

export const useRemoveCartItems = () =>
  useCartMutation(removeCartItems, (cart, itemIds) =>
    withItems(
      cart,
      cart.items.filter((item) => !itemIds.includes(item.id))
    )
  );
